import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { routingRuleService, tenantService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2 } from "lucide-react";

const CHANNEL_TYPES = ["", "webchat", "email", "sms", "whatsapp", "instagram", "facebook", "voice"];
const PRIORITIES = ["", "Low", "Normal", "High", "Urgent"];
const EMPTY = {
  name: "", order: 100, isActive: true,
  conditions: { channelType: "", tag: "", keyword: "", priority: "" },
  action: { assignTeam: "", setPriority: "" }, tenantId: "",
};

export default function RoutingRulesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const canManage = hasPermission("ROUTING.MANAGE");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await routingRuleService.list(); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      ...EMPTY, ...r,
      conditions: { channelType: "", tag: "", keyword: "", priority: "", ...(r.conditions || {}) },
      action: { assignTeam: "", setPriority: "", ...(r.action || {}) },
    });
    setOpen(true);
  };

  const clean = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, v === "" ? null : v]));

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const payload = {
        name: form.name, order: Number(form.order), isActive: form.isActive,
        conditions: clean(form.conditions), action: clean(form.action),
      };
      if (editing) { await routingRuleService.update(editing.id, payload); toast.success("Rule updated"); }
      else { await routingRuleService.create({ ...payload, tenantId: form.tenantId }); toast.success("Rule created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try { await routingRuleService.remove(r.id); toast.success("Rule deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const summarize = (cond) => {
    const parts = [];
    if (cond?.channelType) parts.push(`channel=${cond.channelType}`);
    if (cond?.tag) parts.push(`tag=${cond.tag}`);
    if (cond?.keyword) parts.push(`kw="${cond.keyword}"`);
    if (cond?.priority) parts.push(`priority=${cond.priority}`);
    return parts.join(", ") || "—";
  };

  const columns = [
    { key: "order", header: "Order", render: (r) => <span className="font-mono text-xs text-muted-foreground">{r.order}</span> },
    { key: "name", header: "Rule", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "conditions", header: "When", render: (r) => <span className="text-xs text-muted-foreground">{summarize(r.conditions)}</span> },
    { key: "action", header: "Then", render: (r) => <span className="text-xs text-muted-foreground">{[r.action?.assignTeam && `→ ${r.action.assignTeam}`, r.action?.setPriority && `priority ${r.action.setPriority}`].filter(Boolean).join(", ") || "—"}</span> },
    { key: "isActive", header: "Active", render: (r) => <Badge color={r.isActive ? "green" : "slate"}>{r.isActive ? "Active" : "Off"}</Badge> },
    {
      key: "_a", header: "",
      render: (r) => canManage && (
        <div className="flex items-center justify-end gap-1">
          <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`rule-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`rule-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ),
    },
  ];

  return (
    <div data-testid="routing-rules-page">
      <PageHeader title="Routing Rules" subtitle="Evaluated in order on new conversations — first match assigns team / priority"
        actions={canManage && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="rule-create-btn">
            <Plus className="h-4 w-4" /> New Rule
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="routing-rules-table" empty="No routing rules yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="rule-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Routing Rule</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="rule-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2"><label className="block text-sm font-medium mb-1">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="rule-name" /></div>
                <div><label className="block text-sm font-medium mb-1">Order</label><input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} className="ciq-input" /></div>
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-1">When (conditions)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Channel type</label><select value={form.conditions.channelType} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, channelType: e.target.value } })} className="ciq-input" data-testid="rule-cond-channel">{CHANNEL_TYPES.map((c) => <option key={c} value={c}>{c || "any"}</option>)}</select></div>
                <div><label className="block text-sm font-medium mb-1">Tag</label><input value={form.conditions.tag} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, tag: e.target.value } })} className="ciq-input" data-testid="rule-cond-tag" /></div>
                <div><label className="block text-sm font-medium mb-1">Keyword (in subject)</label><input value={form.conditions.keyword} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, keyword: e.target.value } })} className="ciq-input" /></div>
                <div><label className="block text-sm font-medium mb-1">Priority</label><select value={form.conditions.priority} onChange={(e) => setForm({ ...form, conditions: { ...form.conditions, priority: e.target.value } })} className="ciq-input">{PRIORITIES.map((p) => <option key={p} value={p}>{p || "any"}</option>)}</select></div>
              </div>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground pt-1">Then (action)</p>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Assign team</label><input value={form.action.assignTeam} onChange={(e) => setForm({ ...form, action: { ...form.action, assignTeam: e.target.value } })} className="ciq-input" data-testid="rule-action-team" /></div>
                <div><label className="block text-sm font-medium mb-1">Set priority</label><select value={form.action.setPriority} onChange={(e) => setForm({ ...form, action: { ...form.action, setPriority: e.target.value } })} className="ciq-input" data-testid="rule-action-priority">{PRIORITIES.map((p) => <option key={p} value={p}>{p || "—"}</option>)}</select></div>
              </div>
              <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} data-testid="rule-active" /> Active</label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="rule-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
